import { useStore } from '../store/store';

const ANON_EMAIL_DOMAIN = 'voery.app';

/**
 * Truegate deferred-email flow.
 *
 * The truegate billing backend (core.recurrentbusiness.com) keys the customer —
 * and the user account it creates alongside it — by email, so a purchase cannot
 * be made with no email at all. Instead we create the customer with a synthetic
 * per-user address and collect the real one afterwards on the
 * `email-after-payment` page, which ties it to the purchase via
 * POST /truegate/claim.
 *
 * This address exists only to satisfy that billing backend — it is never shown to
 * the user, and never sent to the Voery API, which identifies the purchase by its
 * transaction id and rejects placeholder addresses outright.
 *
 * NOTE: deliberately `anon-<uuid>@`, not `anon+<uuid>@`. Plus-addressing is
 * normalized away by many backends, which would collapse every anonymous user
 * into a single `anon@voery.app` customer — exactly what this avoids.
 */

function generateUuid(): string {
  const cryptoObj = typeof crypto !== 'undefined' ? crypto : undefined;

  if (cryptoObj?.randomUUID) {
    return cryptoObj.randomUUID();
  }

  if (cryptoObj?.getRandomValues) {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }

  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 10)}`;
}

/**
 * Returns the anonymous email for this user, generating and persisting it on the
 * first call. Stable afterwards (the zustand store is persisted to
 * localStorage), so a reload or a retried payment reuses the same customer.
 */
export function ensureAnonEmail(): string {
  const { anonEmail, setAnonEmail } = useStore.getState();

  if (anonEmail) {
    return anonEmail;
  }

  const generated = `anon-${generateUuid()}@${ANON_EMAIL_DOMAIN}`;
  setAnonEmail(generated);

  return generated;
}

/** True for addresses produced by `ensureAnonEmail` — never show these to the user. */
export function isAnonEmail(value?: string | null): boolean {
  return !!value && /^anon-[^@]+@voery\.app$/i.test(value);
}
